import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Save, Upload, X } from "lucide-react";

const COMPANY_TYPES = ["Pvt Ltd", "Public Ltd", "LLP", "Partnership", "NGO", "Trust", "Proprietary"];
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

const schema = z.object({
  name: z.string().min(3, "Min 3 characters"),
  type: z.enum(COMPANY_TYPES),
  registration_number: z.string().optional().or(z.literal("")),
  pan_number: z.string().regex(PAN_RE, "Invalid PAN (AAAAA9999A)").optional().or(z.literal("")),
  gstin: z.string().regex(GSTIN_RE, "Invalid GSTIN").optional().or(z.literal("")),
  incorporation_date: z.string().optional().or(z.literal(""))
    .refine((v) => !v || new Date(v) <= new Date(), "Date cannot be in future"),
  is_80g_enabled: z.boolean().default(false),
  address: z.string().optional().or(z.literal("")),
  contact_email: z.string().email("Invalid email").optional().or(z.literal("")),
  contact_phone: z.string().regex(/^[6-9]\d{9}$/, "10-digit Indian mobile").optional().or(z.literal("")),
});

function Required() { return <span className="text-rose-600">*</span>; }

export default function OrganizationForm() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [logoB64, setLogoB64] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register, handleSubmit, watch, setValue, formState: { errors }, reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { is_80g_enabled: false, type: "Pvt Ltd" },
  });

  const type = watch("type");
  const showTax80G = type === "NGO" || type === "Trust";

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/organizations/${id}`);
        reset({
          name: data.name || "",
          type: data.type,
          registration_number: data.registration_number || "",
          pan_number: data.pan_number || "",
          gstin: data.gstin || "",
          incorporation_date: data.incorporation_date ? data.incorporation_date.slice(0, 10) : "",
          is_80g_enabled: !!data.is_80g_enabled,
          address: data.address || "",
          contact_email: data.contact_email || "",
          contact_phone: data.contact_phone || "",
        });
        setLogoB64(data.logo_base64 || null);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      }
    })();
  }, [id, isEdit, reset]);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be < 2MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Image files only"); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoB64(reader.result);
    reader.readAsDataURL(file);
  };

  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      const payload = { ...values, logo_base64: logoB64 };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (isEdit) {
        await api.put(`/organizations/${id}`, payload);
        toast.success("Organization updated");
      } else {
        await api.post("/organizations", payload);
        toast.success("Organization created");
      }
      navigate("/organizations");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="organization-form-page" className="mx-auto max-w-4xl">
      <Button variant="ghost" size="sm" asChild className="mb-3 rounded-sm text-slate-600">
        <Link to="/organizations"><ArrowLeft size={14} className="mr-1" />Back</Link>
      </Button>
      <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
        {isEdit ? "Edit Organization" : "Create Organization"}
      </h1>
      <p className="mt-1 text-sm text-slate-500">All fields marked <Required /> are mandatory.</p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6 rounded-sm border border-slate-200 bg-white p-6">
        <Section title="General">
          <Field label="Organization Name" required error={errors.name?.message}>
            <Input data-testid="org-name-input" {...register("name")} className="rounded-sm" />
          </Field>
          <Field label="Company Type" required error={errors.type?.message}>
            <Select value={type} onValueChange={(v) => setValue("type", v, { shouldValidate: true })}>
              <SelectTrigger data-testid="org-type-select" className="rounded-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {COMPANY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Incorporation Date" error={errors.incorporation_date?.message}>
            <Input type="date" data-testid="org-incdate-input" {...register("incorporation_date")} className="rounded-sm" max={new Date().toISOString().slice(0, 10)} />
          </Field>
          <Field label="Registration No / CIN" error={errors.registration_number?.message}>
            <Input data-testid="org-regno-input" {...register("registration_number")} className="rounded-sm font-mono" />
          </Field>
          <Field label="Logo" hint="Image only, max 2MB">
            <div className="flex items-center gap-3">
              {logoB64 ? (
                <div className="relative">
                  <img src={logoB64} alt="" className="h-14 w-14 rounded-sm border border-slate-200 object-cover" />
                  <button type="button" onClick={() => setLogoB64(null)} className="absolute -right-2 -top-2 rounded-full bg-white p-0.5 shadow ring-1 ring-slate-200">
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-sm bg-slate-50 text-slate-400 ring-1 ring-slate-200"><Upload size={16} /></div>
              )}
              <input type="file" accept="image/*" onChange={onLogo} data-testid="org-logo-input"
                className="block text-sm file:mr-3 file:rounded-sm file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-white hover:file:bg-slate-800" />
            </div>
          </Field>
        </Section>

        <Section title="Tax & Legal">
          <Field label="GSTIN" hint="15-char Indian GSTIN" error={errors.gstin?.message}>
            <Input data-testid="org-gstin-input" {...register("gstin")} className="rounded-sm font-mono" placeholder="22AAAAA0000A1Z5" />
          </Field>
          <Field label="PAN" hint="AAAAA9999A" error={errors.pan_number?.message}>
            <Input data-testid="org-pan-input" {...register("pan_number")} className="rounded-sm font-mono" placeholder="ABCDE1234F" />
          </Field>
          {showTax80G && (
            <div className="sm:col-span-2 flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 p-3">
              <div>
                <div className="text-sm font-medium text-slate-900">80G Compliance</div>
                <div className="text-xs text-slate-500">Enable if this {type} has Section 80G certification.</div>
              </div>
              <Switch data-testid="org-80g-switch" checked={watch("is_80g_enabled")} onCheckedChange={(v) => setValue("is_80g_enabled", v)} />
            </div>
          )}
        </Section>

        <Section title="Contact">
          <Field label="Email" error={errors.contact_email?.message}>
            <Input type="email" data-testid="org-email-input" {...register("contact_email")} className="rounded-sm" />
          </Field>
          <Field label="Phone (Indian 10-digit)" error={errors.contact_phone?.message}>
            <Input data-testid="org-phone-input" {...register("contact_phone")} className="rounded-sm font-mono" placeholder="9876543210" />
          </Field>
          <Field label="Address" full>
            <Textarea data-testid="org-address-input" {...register("address")} rows={2} className="rounded-sm" />
          </Field>
        </Section>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate("/organizations")} className="rounded-sm">Cancel</Button>
          <Button type="submit" disabled={submitting} data-testid="org-submit-btn" className="rounded-sm bg-[#002FA7] hover:bg-[#00227A]">
            <Save size={14} className="mr-1.5" />
            {submitting ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="mb-4 border-b border-slate-200 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">{title}</div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}

function Field({ label, required, error, hint, full, children }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="mb-1 block text-sm font-medium text-slate-700">
        {label} {required && <Required />}
      </Label>
      {children}
      {hint && !error && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
}
